"use client";

import { useSyncExternalStore } from "react";
import { type IScheduledCampaign, isCampaignWindowLive } from "@/lib/scheduledCampaign";

// campaigns are scheduled to the minute
const CHECK_INTERVAL_MS = 60_000;

/** Re-check every minute, and as soon as the tab is shown again: background tabs are throttled and sleeping devices stop timers. */
function subscribeToClock(onChange: () => void) {
  const onVisibility = () => {
    if (document.visibilityState === "visible") onChange();
  };
  document.addEventListener("visibilitychange", onVisibility);
  const id = window.setInterval(onChange, CHECK_INTERVAL_MS);
  return () => {
    window.clearInterval(id);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

/**
 * Whether a scheduled campaign is live, checked against the visitor's clock, so cached HTML and
 * open tabs switch on their own.
 *
 * Hydration starts from `initialLive` (what the server rendered with), then the client value
 * takes over. `?<previewParam>=1|0` forces the state for QA, only while the campaign is enabled.
 */
export function useCampaignLive(
  config: IScheduledCampaign | null | undefined,
  previewParam: string,
  initialLive = false,
): boolean {
  const getSnapshot = () => {
    if (!config?.isEnabled) return false;
    const preview = new URLSearchParams(window.location.search).get(previewParam);
    if (preview === "1" || preview === "0") return preview === "1";
    return isCampaignWindowLive(config);
  };

  return useSyncExternalStore(subscribeToClock, getSnapshot, () => initialLive);
}
